import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { 
  Upload, AlertTriangle, CheckCircle, XCircle, Clock, 
  Send, RefreshCw, Eye, CreditCard, Settings, Zap,
  FileSpreadsheet, Link, Ban, PlayCircle, History, Euro
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { confirmAsync } from '@/lib/confirm';

// Status badges
const STATUS_CONFIG = {
  abierto: { label: 'Abierto', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  cerrado: { label: 'Cerrado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  suspendido: { label: 'Suspendido', color: 'bg-red-100 text-red-800', icon: Ban },
  cancelado: { label: 'Cancelado', color: 'bg-gray-100 text-gray-800', icon: XCircle }
};

const STEP_LABELS = {
  0: 'Inicial',
  1: 'Aviso 1 enviado',
  2: 'Aviso 2 enviado',
  3: 'Aviso 3 enviado',
  4: 'Servicio suspendido'
};

export default function Workflows({ user }) {
  const [activeTab, setActiveTab] = useState('dunning');
  const [dunningCases, setDunningCases] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [dunningConfig, setDunningConfig] = useState(null);
  const [invoiceEmailConfig, setInvoiceEmailConfig] = useState(null);
  const [invoiceEmailError, setInvoiceEmailError] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Detail dialog
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Stripe config dialog
  const [showStripeDialog, setShowStripeDialog] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [savingStripe, setSavingStripe] = useState(false);
  
  // Filter
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchDunningCases();
    fetchAutomations();
    fetchStripeConfig();
    fetchDunningConfig();
    fetchInvoiceEmailConfig();
    fetchEmailTemplates();
  }, []);

  const fetchDunningCases = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await axiosInstance.get('/workflows/dunning-cases', { params });
      setDunningCases(response.data);
    } catch (error) {
      toast.error('Error al cargar casos de impago');
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomations = async () => {
    try {
      const response = await axiosInstance.get('/workflows/automations');
      setAutomations(response.data);
    } catch (error) {
      console.error('Error fetching automations:', error);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const response = await axiosInstance.get('/workflows/stripe-config');
      setStripeConfig(response.data);
    } catch (error) {
      console.error('Error fetching stripe config:', error);
    }
  };

  const fetchDunningConfig = async () => {
    try {
      const response = await axiosInstance.get('/workflows/dunning-config');
      setDunningConfig(response.data);
    } catch (error) {
      console.error('Error fetching dunning config:', error);
    }
  };

  const fetchInvoiceEmailConfig = async () => {
    try {
      const response = await axiosInstance.get('/workflows/invoice-email-config');
      setInvoiceEmailConfig(response.data);
      setInvoiceEmailError(null);
    } catch (error) {
      // Sin esto la pestaña se quedaba vacia y muda: el error solo iba a la
      // consola y la condicion de render devolvia null.
      console.error('Error fetching invoice email config:', error);
      setInvoiceEmailError(
        error.response?.data?.detail
        || `No se pudo cargar la configuración (error ${error.response?.status || 'de red'})`
      );
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axiosInstance.get('/workflows/email-templates-list');
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const handleSaveDunningConfig = async () => {
    try {
      await axiosInstance.put('/workflows/dunning-config', dunningConfig);
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  const handleSaveInvoiceEmailConfig = async () => {
    try {
      await axiosInstance.put('/workflows/invoice-email-config', invoiceEmailConfig);
      toast.success('Configuración de emails de factura guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axiosInstance.post(
        '/workflows/import-sepa-returns?bank_name=Santander',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResult(response.data);
      toast.success(`Importación completada: ${response.data.matched} coincidencias`);
      fetchDunningCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  const handleCloseCase = async (caseId) => {
    if (!(await confirmAsync('¿Cerrar este caso de impago? Se marcará la factura como pagada.'))) return;
    
    try {
      await axiosInstance.post(`/workflows/dunning-cases/${caseId}/close`, {
        reason: 'Cerrado manualmente'
      });
      toast.success('Caso cerrado correctamente');
      fetchDunningCases();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caso');
    }
  };

  const handleRetryStep = async (caseId) => {
    try {
      await axiosInstance.post(`/workflows/dunning-cases/${caseId}/retry`);
      toast.success('Paso reintentado');
      fetchDunningCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reintentar');
    }
  };

  const handleSaveStripeConfig = async () => {
    if (!stripeKey) {
      toast.error('Introduce la API Key de Stripe');
      return;
    }
    
    setSavingStripe(true);
    try {
      await axiosInstance.post('/workflows/stripe-config', {
        stripe_api_key: stripeKey
      });
      toast.success('Configuración de Stripe guardada');
      setShowStripeDialog(false);
      fetchStripeConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingStripe(false);
    }
  };

  const handleInitDefaults = async () => {
    try {
      await axiosInstance.post('/workflows/automations/init-defaults');
      await axiosInstance.post('/workflows/sepa-mappings/init-default');
      toast.success('Configuración por defecto creada');
      fetchAutomations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear configuración');
    }
  };

  const openDetail = (dunningCase) => {
    setSelectedCase(dunningCase);
    setShowDetailDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout user={user}>
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">⚡ Workflows & Automatizaciones</h1>
          <p className="text-gray-500">Gestión de impagos y automatizaciones de email</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowStripeDialog(true)}>
            <CreditCard className="w-4 h-4 mr-2" />
            Config Stripe
          </Button>
          <Button variant="outline" onClick={handleInitDefaults}>
            <Settings className="w-4 h-4 mr-2" />
            Crear Defaults
          </Button>
        </div>
      </div>

      {/* Stripe status */}
      {stripeConfig?.stripe_api_key_masked ? (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800">
              Stripe configurado: {stripeConfig.stripe_api_key_masked}
            </span>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="py-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              Stripe no configurado. Configúralo para generar links de pago.
            </span>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dunning">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Impagos / Dunning
          </TabsTrigger>
          <TabsTrigger value="emails">
            <Send className="w-4 h-4 mr-2" />
            Emails Facturas
          </TabsTrigger>
          <TabsTrigger value="config">
            <Settings className="w-4 h-4 mr-2" />
            Config Dunning
          </TabsTrigger>
          <TabsTrigger value="automations">
            <Zap className="w-4 h-4 mr-2" />
            Automatizaciones
          </TabsTrigger>
        </TabsList>

        {/* DUNNING TAB */}
        <TabsContent value="dunning" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="abierto">Abiertos</SelectItem>
                  <SelectItem value="suspendido">Suspendidos</SelectItem>
                  <SelectItem value="cerrado">Cerrados</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchDunningCases}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <Button onClick={() => setShowImportDialog(true)} className="bg-blue-600">
              <Upload className="w-4 h-4 mr-2" />
              Importar Devoluciones SEPA
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Factura</TableHead>
                    <TableHead>Importe</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Paso</TableHead>
                    <TableHead>Próxima acción</TableHead>
                    <TableHead>Stripe Link</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <>
                      {[1, 2, 3].map((i) => (
                        <TableRow key={`sk-${i}`}>
                          {Array.from({ length: 8 }).map((_, c) => (
                            <TableCell key={c}><Skeleton className="h-5 w-full" /></TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </>
                  ) : dunningCases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No hay casos de impago
                      </TableCell>
                    </TableRow>
                  ) : (
                    dunningCases.map((dc) => {
                      const statusConfig = STATUS_CONFIG[dc.status] || STATUS_CONFIG.abierto;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow key={dc.id}>
                          <TableCell className="font-medium">{dc.customer_name}</TableCell>
                          <TableCell>{dc.invoice_number}</TableCell>
                          <TableCell>
                            <div className="font-semibold">{dc.amount?.toFixed(2)} €</div>
                            {dc.amount_fee > 0 && (
                              <div className="text-xs text-gray-500">
                                ({dc.amount_original?.toFixed(2)} + {dc.amount_fee?.toFixed(2)} gastos)
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">
                              {STEP_LABELS[dc.step] || `Paso ${dc.step}`}
                            </span>
                          </TableCell>
                          <TableCell>
                            {dc.next_action_at ? (
                              <span className="text-xs text-gray-500">
                                {formatDate(dc.next_action_at)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {dc.stripe_link_url ? (
                              <a 
                                href={dc.stripe_link_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Link className="w-3 h-3" />
                                Ver link
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openDetail(dc)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {dc.status === 'abierto' && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => handleRetryStep(dc.id)}>
                                    <PlayCircle className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleCloseCase(dc.id)}>
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMAILS FACTURAS TAB */}
        <TabsContent value="emails" className="space-y-4">
          {invoiceEmailError && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-900 text-base">
                  No se pudo cargar la configuración de emails
                </CardTitle>
                <CardDescription className="text-red-800">
                  {invoiceEmailError}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" onClick={fetchInvoiceEmailConfig}>
                  Reintentar
                </Button>
              </CardContent>
            </Card>
          )}

          {!invoiceEmailConfig && !invoiceEmailError && (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                Cargando configuración de emails...
              </CardContent>
            </Card>
          )}

          {invoiceEmailConfig && (
            <>
              {/* Info */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-3">
                  <p className="text-sm text-blue-800">
                    <strong>Variables disponibles en plantillas:</strong> {'{nombre_cliente}'}, {'{numero_factura}'}, {'{numero_presupuesto}'}, {'{importe}'}, {'{fecha_emision}'}, {'{fecha_vencimiento}'}, {'{forma_pago}'}, {'{link_portal}'}, {'{periodo_mes}'}, {'{periodo_ano}'}
                  </p>
                </CardContent>
              </Card>

              {/* Facturas Repetitivas */}
              <Card>
                <CardHeader>
                  <CardTitle>📅 Facturas Repetitivas (Mensual/Anual)</CardTitle>
                  <CardDescription>
                    Emails automáticos para facturas recurrentes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Recibo/SEPA */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">🏦 Recibo/SEPA</Label>
                        <Switch
                          checked={invoiceEmailConfig.repetitiva_sepa_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_sepa_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.repetitiva_sepa_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_sepa_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Transferencia */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💸 Transferencia</Label>
                        <Switch
                          checked={invoiceEmailConfig.repetitiva_transferencia_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_transferencia_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.repetitiva_transferencia_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_transferencia_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Link Directo/Stripe */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💳 Link Pago Online</Label>
                        <Switch
                          checked={invoiceEmailConfig.repetitiva_link_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_link_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.repetitiva_link_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_link_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Efectivo */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💵 Efectivo</Label>
                        <Switch
                          checked={invoiceEmailConfig.repetitiva_efectivo_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_efectivo_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.repetitiva_efectivo_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, repetitiva_efectivo_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Facturas Únicas */}
              <Card>
                <CardHeader>
                  <CardTitle>📄 Facturas Únicas/Manuales</CardTitle>
                  <CardDescription>
                    Emails automáticos para facturas no recurrentes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Recibo/SEPA */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">🏦 Recibo/SEPA</Label>
                        <Switch
                          checked={invoiceEmailConfig.unica_sepa_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_sepa_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.unica_sepa_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_sepa_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Transferencia */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💸 Transferencia</Label>
                        <Switch
                          checked={invoiceEmailConfig.unica_transferencia_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_transferencia_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.unica_transferencia_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_transferencia_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Link Directo/Stripe */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💳 Link Pago Online</Label>
                        <Switch
                          checked={invoiceEmailConfig.unica_link_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_link_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.unica_link_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_link_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Efectivo */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💵 Efectivo</Label>
                        <Switch
                          checked={invoiceEmailConfig.unica_efectivo_emision_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_efectivo_emision_activo: v})}
                        />
                      </div>
                      <Select 
                        value={invoiceEmailConfig.unica_efectivo_emision_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, unica_efectivo_emision_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pago Recibido y Post-Pago */}
              <Card>
                <CardHeader>
                  <CardTitle>✅ Confirmaciones de Pago</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Pago Recibido */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">💰 Pago Recibido</Label>
                        <Switch
                          checked={invoiceEmailConfig.pago_recibido_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, pago_recibido_activo: v})}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Se envía cuando se marca la factura como pagada</p>
                      <Select 
                        value={invoiceEmailConfig.pago_recibido_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, pago_recibido_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Factura Post-Pago */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold">🧾 Factura Post-Pago</Label>
                        <Switch
                          checked={invoiceEmailConfig.factura_post_pago_activo ?? true}
                          onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, factura_post_pago_activo: v})}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Para facturas creadas después del pago (ej: Stripe)</p>
                      <Select 
                        value={invoiceEmailConfig.factura_post_pago_plantilla || ''} 
                        onValueChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, factura_post_pago_plantilla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Opciones Generales */}
              <Card>
                <CardHeader>
                  <CardTitle>⚙️ Opciones Generales</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="font-medium">Adjuntar PDF de factura</Label>
                      <p className="text-sm text-gray-500">Incluir el PDF de la factura como adjunto</p>
                    </div>
                    <Switch
                      checked={invoiceEmailConfig.adjuntar_pdf ?? true}
                      onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, adjuntar_pdf: v})}
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="font-medium">Incluir link al portal del cliente</Label>
                      <p className="text-sm text-gray-500">Añadir enlace para descargar factura desde el portal</p>
                    </div>
                    <Switch
                      checked={invoiceEmailConfig.incluir_link_portal ?? true}
                      onCheckedChange={(v) => setInvoiceEmailConfig({...invoiceEmailConfig, incluir_link_portal: v})}
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                    />
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="font-medium">URL del portal del cliente</Label>
                    <Input
                      value={invoiceEmailConfig.portal_url || ''}
                      onChange={(e) => setInvoiceEmailConfig({...invoiceEmailConfig, portal_url: e.target.value})}
                      placeholder="https://tu-dominio.com/portal-cliente"
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Déjalo vacío para usar la URL del sistema</p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSaveInvoiceEmailConfig} className="bg-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar Configuración de Emails
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* CONFIG TAB */}
        <TabsContent value="config" className="space-y-4">
          {dunningConfig && (
            <>
              {/* Recargos por devolución */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Euro className="w-5 h-5" />
                    Recargos por Devolución SEPA
                  </CardTitle>
                  <CardDescription>
                    Gastos bancarios que se añaden al importe de la factura devuelta
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Umbral de importe (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={dunningConfig.umbral_recargo || 100}
                        onChange={(e) => setDunningConfig({...dunningConfig, umbral_recargo: parseFloat(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Importe que divide los dos recargos</p>
                    </div>
                    <div>
                      <Label>Recargo ≤ {dunningConfig.umbral_recargo || 100}€</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={dunningConfig.recargo_hasta_100 || 6}
                        onChange={(e) => setDunningConfig({...dunningConfig, recargo_hasta_100: parseFloat(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Recargo para importes pequeños</p>
                    </div>
                    <div>
                      <Label>Recargo &gt; {dunningConfig.umbral_recargo || 100}€</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={dunningConfig.recargo_desde_100 || 12}
                        onChange={(e) => setDunningConfig({...dunningConfig, recargo_desde_100: parseFloat(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Recargo para importes mayores</p>
                    </div>
                  </div>
                  <div>
                    <Label>Texto para recargos en emails</Label>
                    <Input
                      value={dunningConfig.texto_gastos_devolucion || ''}
                      onChange={(e) => setDunningConfig({...dunningConfig, texto_gastos_devolucion: e.target.value})}
                      placeholder="Gastos de devolución bancaria"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Plantillas de email */}
              <Card>
                <CardHeader>
                  <CardTitle>Plantillas de Email para Dunning</CardTitle>
                  <CardDescription>
                    Selecciona qué plantilla usar en cada paso del proceso de recobro
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Aviso 1 (D0 - Primer contacto)</Label>
                      <Select 
                        value={dunningConfig.plantilla_aviso_1 || ''} 
                        onValueChange={(v) => setDunningConfig({...dunningConfig, plantilla_aviso_1: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Aviso 2 (D+{dunningConfig.dias_aviso_2 || 1})</Label>
                      <Select 
                        value={dunningConfig.plantilla_aviso_2 || ''} 
                        onValueChange={(v) => setDunningConfig({...dunningConfig, plantilla_aviso_2: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Aviso 3 - Amenaza (D+{(dunningConfig.dias_aviso_2 || 1) + (dunningConfig.dias_aviso_3 || 1)})</Label>
                      <Select 
                        value={dunningConfig.plantilla_aviso_3 || ''} 
                        onValueChange={(v) => setDunningConfig({...dunningConfig, plantilla_aviso_3: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Suspensión de servicio</Label>
                      <Select 
                        value={dunningConfig.plantilla_suspension || ''} 
                        onValueChange={(v) => setDunningConfig({...dunningConfig, plantilla_suspension: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plantilla" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Días entre avisos */}
              <Card>
                <CardHeader>
                  <CardTitle>Programación de Avisos</CardTitle>
                  <CardDescription>
                    Días que pasan entre cada aviso del proceso de recobro
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Días hasta Aviso 2</Label>
                      <Input
                        type="number"
                        min="1"
                        value={dunningConfig.dias_aviso_2 || 1}
                        onChange={(e) => setDunningConfig({...dunningConfig, dias_aviso_2: parseInt(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Después del Aviso 1</p>
                    </div>
                    <div>
                      <Label>Días hasta Aviso 3</Label>
                      <Input
                        type="number"
                        min="1"
                        value={dunningConfig.dias_aviso_3 || 1}
                        onChange={(e) => setDunningConfig({...dunningConfig, dias_aviso_3: parseInt(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Después del Aviso 2</p>
                    </div>
                    <div>
                      <Label>Días hasta Suspensión</Label>
                      <Input
                        type="number"
                        min="1"
                        value={dunningConfig.dias_suspension || 1}
                        onChange={(e) => setDunningConfig({...dunningConfig, dias_suspension: parseInt(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Después del Aviso 3</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Opciones */}
              <Card>
                <CardHeader>
                  <CardTitle>Opciones de Recobro</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Suspender servicio automáticamente</Label>
                      <p className="text-sm text-gray-500">Bloquear acceso a plataforma C10 tras el último aviso</p>
                    </div>
                    <Switch
                      checked={dunningConfig.suspender_servicio ?? true}
                      onCheckedChange={(v) => setDunningConfig({...dunningConfig, suspender_servicio: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Reactivar al recibir pago</Label>
                      <p className="text-sm text-gray-500">Desbloquear acceso automáticamente al pagar</p>
                    </div>
                    <Switch
                      checked={dunningConfig.reactivar_al_pagar ?? true}
                      onCheckedChange={(v) => setDunningConfig({...dunningConfig, reactivar_al_pagar: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enviar email al recibir pago</Label>
                      <p className="text-sm text-gray-500">Notificar al cliente que su pago ha sido recibido</p>
                    </div>
                    <Switch
                      checked={dunningConfig.enviar_email_pago ?? true}
                      onCheckedChange={(v) => setDunningConfig({...dunningConfig, enviar_email_pago: v})}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSaveDunningConfig} className="bg-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar Configuración
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* AUTOMATIONS TAB */}
        <TabsContent value="automations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Automatizaciones Activas</CardTitle>
              <CardDescription>
                Reglas que se ejecutan automáticamente al emitir o pagar facturas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {automations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay automatizaciones configuradas</p>
                  <Button className="mt-4" onClick={handleInitDefaults}>
                    Crear automatizaciones por defecto
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {automations.map((auto) => (
                    <div 
                      key={auto.id} 
                      className="border rounded-lg p-4 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          <Zap className={`w-4 h-4 ${auto.is_active ? 'text-yellow-500' : 'text-gray-400'}`} />
                          {auto.name}
                        </div>
                        <p className="text-sm text-gray-500">{auto.description}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline">{auto.trigger}</Badge>
                          {Object.entries(auto.conditions || {}).map(([k, v]) => (
                            <Badge key={k} variant="secondary">{k}: {v}</Badge>
                          ))}
                        </div>
                      </div>
                      <Badge className={auto.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                        {auto.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* IMPORT DIALOG */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar Devoluciones SEPA</DialogTitle>
            <DialogDescription>
              Sube el Excel de devoluciones del banco para crear casos de recobro automáticamente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="max-w-xs mx-auto"
              />
              {importFile && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ {importFile.name}
                </p>
              )}
            </div>
            
            {importResult && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium">Resultado de importación:</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{importResult.total_rows}</div>
                    <div className="text-xs text-gray-500">Total filas</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{importResult.matched}</div>
                    <div className="text-xs text-gray-500">Coincidencias</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-600">{importResult.unmatched}</div>
                    <div className="text-xs text-gray-500">Sin match</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-600">{importResult.duplicates}</div>
                    <div className="text-xs text-gray-500">Duplicados</div>
                  </div>
                </div>
                
                {importResult.unmatched_rows?.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-yellow-800 mb-2">
                      Filas sin coincidencia (requieren revisión manual):
                    </h5>
                    <div className="max-h-40 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Referencia</TableHead>
                            <TableHead>Importe</TableHead>
                            <TableHead>Deudor</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importResult.unmatched_rows.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.reference}</TableCell>
                              <TableCell>{row.amount?.toFixed(2)} €</TableCell>
                              <TableCell>{row.debtor}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cerrar
            </Button>
            <Button onClick={handleImport} disabled={!importFile || importing}>
              {importing ? 'Importando...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle del Caso de Impago</DialogTitle>
          </DialogHeader>
          
          {selectedCase && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Cliente</Label>
                  <p className="font-medium">{selectedCase.customer_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Factura</Label>
                  <p className="font-medium">{selectedCase.invoice_number}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Importe</Label>
                  <p className="font-bold text-xl">{selectedCase.amount?.toFixed(2)} €</p>
                </div>
                <div>
                  <Label className="text-gray-500">Estado</Label>
                  <Badge className={STATUS_CONFIG[selectedCase.status]?.color}>
                    {STATUS_CONFIG[selectedCase.status]?.label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Motivo devolución</Label>
                  <p>{selectedCase.sepa_return_reason || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Fecha devolución</Label>
                  <p>{formatDate(selectedCase.sepa_return_date)}</p>
                </div>
              </div>
              
              {selectedCase.stripe_link_url && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Label className="text-blue-800">Link de pago Stripe</Label>
                  <a 
                    href={selectedCase.stripe_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {selectedCase.stripe_link_url}
                  </a>
                </div>
              )}
              
              {/* Historial */}
              <div>
                <Label className="text-gray-500 flex items-center gap-2 mb-2">
                  <History className="w-4 h-4" />
                  Historial de acciones
                </Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {selectedCase.logs?.map((log, idx) => (
                    <div key={idx} className="px-3 py-2 border-b last:border-b-0 text-sm">
                      <span className="text-gray-400 mr-2">
                        {formatDate(log.timestamp)}
                      </span>
                      <Badge variant="outline" className="mr-2">{log.action}</Badge>
                      <span className="text-gray-600">{log.details}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedCase?.status === 'abierto' && (
              <>
                <Button variant="outline" onClick={() => handleRetryStep(selectedCase.id)}>
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Reintentar paso
                </Button>
                <Button className="bg-green-600" onClick={() => handleCloseCase(selectedCase.id)}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Cerrar (marcar pagado)
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STRIPE CONFIG DIALOG */}
      <Dialog open={showStripeDialog} onOpenChange={setShowStripeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configuración de Stripe</DialogTitle>
            <DialogDescription>
              Introduce la API Key de Stripe para generar links de pago
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Stripe API Key</Label>
              <Input
                type="password"
                placeholder="rk_live_... o sk_live_..."
                value={stripeKey}
                onChange={(e) => setStripeKey(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Obtén tu key en dashboard.stripe.com/apikeys
              </p>
            </div>
            
            {stripeConfig?.stripe_api_key_masked && (
              <div className="text-sm text-green-600">
                Key actual: {stripeConfig.stripe_api_key_masked}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStripeDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveStripeConfig} disabled={savingStripe}>
              {savingStripe ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
